import httpClient from './httpClient';
import type { PaginatedResult } from '@/types';
import type {
  OrganizationItem, OrganizationFilters, CreateOrganizationRequest, UpdateOrganizationRequest,
  CounterpartyItem, CounterpartyFilters, CreateCounterpartyRequest, UpdateCounterpartyRequest,
  CurrencyItem, CreateCurrencyRequest, UpdateCurrencyRequest,
  BankItem, CreateBankRequest, UpdateBankRequest,
  BankAccountItem, BankAccountFilters, CreateBankAccountRequest, UpdateBankAccountRequest,
  NomenclatureGroupItem, CreateNomenclatureGroupRequest, UpdateNomenclatureGroupRequest,
  NomenclatureItem, NomenclatureFilters, CreateNomenclatureRequest, UpdateNomenclatureRequest,
  AccountingWarehouseItem, CreateWarehouseRequest, UpdateWarehouseRequest,
  CostItemItem, CreateCostItemRequest, UpdateCostItemRequest,
  PriceTypeItem, CreatePriceTypeRequest, UpdatePriceTypeRequest,
  ChartOfAccountItem, ChartOfAccountFilters, CreateChartOfAccountRequest, UpdateChartOfAccountRequest,
  ImportChartOfAccountsResult,
  TaxActivityTypeItem, CreateTaxActivityTypeRequest, UpdateTaxActivityTypeRequest,
  SettlementTypeItem, CreateSettlementTypeRequest, UpdateSettlementTypeRequest,
  OrgDepartmentItem, CreateOrgDepartmentRequest, UpdateOrgDepartmentRequest,
  VatRateItem,
  ContractItem, ContractFilters, CreateContractRequest, UpdateContractRequest,
  CurrencyRateItem, CreateCurrencyRateRequest, UpdateCurrencyRateRequest,
  NomenclaturePriceItem,
  AccountingPolicyItem,
  AccountBalanceItem,
  TrialBalanceLine, TrialBalanceFilters,
  JournalEntryItem, JournalEntryFilters,
  IndividualItem, IndividualFilters, CreateIndividualRequest, UpdateIndividualRequest,
  EmployeeItem, EmployeeFilters, CreateEmployeeRequest, UpdateEmployeeRequest,
  // Position-types removed (Етап 4f)
  FixedAssetItem, CreateFixedAssetRequest, UpdateFixedAssetRequest,
  IntangibleAssetItem, CreateIntangibleAssetRequest, UpdateIntangibleAssetRequest,
} from '@/types/accounting';

export const accountingApi = {
  // === Organizations — Phase 7f: Domain entity видалено, ходимо через unified-ендпоінт.
  // /api/organizations не існує, /api/references/organization → PagedResult<ReferenceItemDto>{id,code,label}.
  // Мапимо у форму PaginatedResult<OrganizationItem> для backward-compat існуючих усивачів.
  async getOrganizations(acqFilters: OrganizationFilters): Promise<PaginatedResult<OrganizationItem>> {
    const acqParams: Record<string, unknown> = { qPage: acqFilters.page, qPageSize: acqFilters.pageSize };
    if (acqFilters.search) acqParams.qSearch = acqFilters.search;
    const acqResponse = await httpClient.get<{
      items: { id: string; code: string; label: string }[];
      totalCount: number;
      page: number;
      pageSize: number;
    }>('/references/organization', { params: acqParams });
    const acqItems: OrganizationItem[] = acqResponse.data.items.map((acqIt) => ({
      id: acqIt.id,
      code: acqIt.code,
      name: acqIt.label,
    }) as OrganizationItem);
    const acqPageSize = acqResponse.data.pageSize || acqFilters.pageSize || 50;
    const acqTotalCount = acqResponse.data.totalCount || acqItems.length;
    return {
      items: acqItems,
      totalCount: acqTotalCount,
      page: acqResponse.data.page || acqFilters.page || 1,
      pageSize: acqPageSize,
      totalPages: Math.max(1, Math.ceil(acqTotalCount / acqPageSize)),
    };
  },
  // Phase 7f: CRUD організацій тепер через DynamicCatalogItems (POST/PUT/DELETE /api/catalog-items).
  // Старі типізовані ендпоінти видалено — ці методи більше не викликаються з UI (CatalogTypesPage
  // → DynamicCatalogItemsPage веде роботу з організаціями як з звичайним meta-каталогом).
  async createOrganization(_acqReq: CreateOrganizationRequest): Promise<string> {
    throw new Error('Створення організації — через DynamicCatalogItemsPage (typeCode="organization")');
  },
  async updateOrganization(_acqReq: UpdateOrganizationRequest): Promise<void> {
    throw new Error('Редагування організації — через DynamicCatalogItemsPage (typeCode="organization")');
  },
  async deleteOrganization(_acqId: string): Promise<void> {
    throw new Error('Видалення організації — через DynamicCatalogItemsPage (typeCode="organization")');
  },

  // === Counterparties (paginated) ===
  async getCounterparties(acqFilters: CounterpartyFilters): Promise<PaginatedResult<CounterpartyItem>> {
    const acqParams: Record<string, unknown> = { page: acqFilters.page, pageSize: acqFilters.pageSize };
    if (acqFilters.search) acqParams.search = acqFilters.search;
    if (acqFilters.isGroup !== undefined) acqParams.isGroup = acqFilters.isGroup;
    const acqResponse = await httpClient.get<PaginatedResult<CounterpartyItem>>('/counterparties', { params: acqParams });
    return acqResponse.data;
  },
  async createCounterparty(acqReq: CreateCounterpartyRequest): Promise<string> {
    const acqResponse = await httpClient.post<string>('/counterparties', acqReq);
    return acqResponse.data;
  },
  async updateCounterparty(acqReq: UpdateCounterpartyRequest): Promise<void> {
    await httpClient.put(`/counterparties/${acqReq.id}`, acqReq);
  },
  async deleteCounterparty(acqId: string): Promise<void> {
    await httpClient.delete(`/counterparties/${acqId}`);
  },

  // === Currencies (list) ===
  async getCurrencies(): Promise<CurrencyItem[]> {
    const acqResponse = await httpClient.get<CurrencyItem[]>('/currencies');
    return acqResponse.data;
  },
  async createCurrency(acqReq: CreateCurrencyRequest): Promise<string> {
    const acqResponse = await httpClient.post<string>('/currencies', acqReq);
    return acqResponse.data;
  },
  async updateCurrency(acqReq: UpdateCurrencyRequest): Promise<void> {
    await httpClient.put(`/currencies/${acqReq.id}`, acqReq);
  },
  async deleteCurrency(acqId: string): Promise<void> {
    await httpClient.delete(`/currencies/${acqId}`);
  },

  // === Banks (list) ===
  async getBanks(): Promise<BankItem[]> {
    const acqResponse = await httpClient.get<BankItem[]>('/banks');
    return acqResponse.data;
  },
  async createBank(acqReq: CreateBankRequest): Promise<string> {
    const acqResponse = await httpClient.post<string>('/banks', acqReq);
    return acqResponse.data;
  },
  async updateBank(acqReq: UpdateBankRequest): Promise<void> {
    await httpClient.put(`/banks/${acqReq.id}`, acqReq);
  },
  async deleteBank(acqId: string): Promise<void> {
    await httpClient.delete(`/banks/${acqId}`);
  },

  // === Bank Accounts (paginated) ===
  async getBankAccounts(acqFilters: BankAccountFilters): Promise<PaginatedResult<BankAccountItem>> {
    const acqParams: Record<string, unknown> = { page: acqFilters.page, pageSize: acqFilters.pageSize };
    if (acqFilters.ownerType) acqParams.ownerType = acqFilters.ownerType;
    if (acqFilters.ownerId) acqParams.ownerId = acqFilters.ownerId;
    const acqResponse = await httpClient.get<PaginatedResult<BankAccountItem>>('/bank-accounts', { params: acqParams });
    return acqResponse.data;
  },
  async createBankAccount(acqReq: CreateBankAccountRequest): Promise<string> {
    const acqResponse = await httpClient.post<string>('/bank-accounts', acqReq);
    return acqResponse.data;
  },
  async updateBankAccount(acqReq: UpdateBankAccountRequest): Promise<void> {
    await httpClient.put(`/bank-accounts/${acqReq.id}`, acqReq);
  },
  async deleteBankAccount(acqId: string): Promise<void> {
    await httpClient.delete(`/bank-accounts/${acqId}`);
  },

  // === Nomenclature Groups (list) ===
  async getNomenclatureGroups(): Promise<NomenclatureGroupItem[]> {
    const acqResponse = await httpClient.get<NomenclatureGroupItem[]>('/nomenclature-groups');
    return acqResponse.data;
  },
  async createNomenclatureGroup(acqReq: CreateNomenclatureGroupRequest): Promise<string> {
    const acqResponse = await httpClient.post<string>('/nomenclature-groups', acqReq);
    return acqResponse.data;
  },
  async updateNomenclatureGroup(acqReq: UpdateNomenclatureGroupRequest): Promise<void> {
    await httpClient.put(`/nomenclature-groups/${acqReq.id}`, acqReq);
  },
  async deleteNomenclatureGroup(acqId: string): Promise<void> {
    await httpClient.delete(`/nomenclature-groups/${acqId}`);
  },

  // === Nomenclatures (paginated) ===
  async getNomenclatures(acqFilters: NomenclatureFilters): Promise<PaginatedResult<NomenclatureItem>> {
    const acqParams: Record<string, unknown> = { page: acqFilters.page, pageSize: acqFilters.pageSize };
    if (acqFilters.search) acqParams.search = acqFilters.search;
    if (acqFilters.isGroup !== undefined) acqParams.isGroup = acqFilters.isGroup;
    if (acqFilters.isService !== undefined) acqParams.isService = acqFilters.isService;
    if (acqFilters.nomenclatureGroupId) acqParams.nomenclatureGroupId = acqFilters.nomenclatureGroupId;
    const acqResponse = await httpClient.get<PaginatedResult<NomenclatureItem>>('/nomenclatures', { params: acqParams });
    return acqResponse.data;
  },
  async createNomenclature(acqReq: CreateNomenclatureRequest): Promise<string> {
    const acqResponse = await httpClient.post<string>('/nomenclatures', acqReq);
    return acqResponse.data;
  },
  async updateNomenclature(acqReq: UpdateNomenclatureRequest): Promise<void> {
    await httpClient.put(`/nomenclatures/${acqReq.id}`, acqReq);
  },
  async deleteNomenclature(acqId: string): Promise<void> {
    await httpClient.delete(`/nomenclatures/${acqId}`);
  },

  // === Accounting Warehouses (list) ===
  async getAccountingWarehouses(): Promise<AccountingWarehouseItem[]> {
    const acqResponse = await httpClient.get<AccountingWarehouseItem[]>('/accounting-warehouses');
    return acqResponse.data;
  },
  async createAccountingWarehouse(acqReq: CreateWarehouseRequest): Promise<string> {
    const acqResponse = await httpClient.post<string>('/accounting-warehouses', acqReq);
    return acqResponse.data;
  },
  async updateAccountingWarehouse(acqReq: UpdateWarehouseRequest): Promise<void> {
    await httpClient.put(`/accounting-warehouses/${acqReq.id}`, acqReq);
  },
  async deleteAccountingWarehouse(acqId: string): Promise<void> {
    await httpClient.delete(`/accounting-warehouses/${acqId}`);
  },

  // === Cost Items (list) ===
  async getCostItems(): Promise<CostItemItem[]> {
    const acqResponse = await httpClient.get<CostItemItem[]>('/cost-items');
    return acqResponse.data;
  },
  async createCostItem(acqReq: CreateCostItemRequest): Promise<string> {
    const acqResponse = await httpClient.post<string>('/cost-items', acqReq);
    return acqResponse.data;
  },
  async updateCostItem(acqReq: UpdateCostItemRequest): Promise<void> {
    await httpClient.put(`/cost-items/${acqReq.id}`, acqReq);
  },
  async deleteCostItem(acqId: string): Promise<void> {
    await httpClient.delete(`/cost-items/${acqId}`);
  },

  // === Price Types (list) ===
  async getPriceTypes(): Promise<PriceTypeItem[]> {
    const acqResponse = await httpClient.get<PriceTypeItem[]>('/price-types');
    return acqResponse.data;
  },
  async createPriceType(acqReq: CreatePriceTypeRequest): Promise<string> {
    const acqResponse = await httpClient.post<string>('/price-types', acqReq);
    return acqResponse.data;
  },
  async updatePriceType(acqReq: UpdatePriceTypeRequest): Promise<void> {
    await httpClient.put(`/price-types/${acqReq.id}`, acqReq);
  },
  async deletePriceType(acqId: string): Promise<void> {
    await httpClient.delete(`/price-types/${acqId}`);
  },

  // === Chart of Accounts (paginated) ===
  async getChartOfAccounts(acqFilters: ChartOfAccountFilters): Promise<PaginatedResult<ChartOfAccountItem>> {
    const acqParams: Record<string, unknown> = { page: acqFilters.page, pageSize: acqFilters.pageSize };
    if (acqFilters.search) acqParams.search = acqFilters.search;
    if (acqFilters.accountType) acqParams.accountType = acqFilters.accountType;
    if (acqFilters.isGroup !== undefined) acqParams.isGroup = acqFilters.isGroup;
    const acqResponse = await httpClient.get<PaginatedResult<ChartOfAccountItem>>('/chart-of-accounts', { params: acqParams });
    return acqResponse.data;
  },
  async createChartOfAccount(acqReq: CreateChartOfAccountRequest): Promise<string> {
    const acqResponse = await httpClient.post<string>('/chart-of-accounts', acqReq);
    return acqResponse.data;
  },
  async updateChartOfAccount(acqReq: UpdateChartOfAccountRequest): Promise<void> {
    await httpClient.put(`/chart-of-accounts/${acqReq.id}`, acqReq);
  },
  async deleteChartOfAccount(acqId: string): Promise<void> {
    await httpClient.delete(`/chart-of-accounts/${acqId}`);
  },
  async getAllChartOfAccounts(): Promise<ChartOfAccountItem[]> {
    const acqResponse = await httpClient.get<ChartOfAccountItem[]>('/chart-of-accounts/all');
    return acqResponse.data;
  },
  async deleteAllChartOfAccounts(): Promise<{ deleted: number }> {
    const acqResponse = await httpClient.delete<{ deleted: number }>('/chart-of-accounts/all');
    return acqResponse.data;
  },
  async downloadChartOfAccountsTemplate(): Promise<Blob> {
    const acqResponse = await httpClient.get('/chart-of-accounts/template', { responseType: 'blob' });
    return acqResponse.data as Blob;
  },
  async exportChartOfAccounts(): Promise<Blob> {
    const acqResponse = await httpClient.get('/chart-of-accounts/export', { responseType: 'blob' });
    return acqResponse.data as Blob;
  },
  async importChartOfAccounts(acqFile: File, acqMode: 'Append' | 'Replace' | 'FullReplace'): Promise<ImportChartOfAccountsResult> {
    const acqForm = new FormData();
    acqForm.append('file', acqFile);
    const acqResponse = await httpClient.post<ImportChartOfAccountsResult>(
      '/chart-of-accounts/import',
      acqForm,
      { params: { mode: acqMode }, headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return acqResponse.data;
  },

  // === Tax Activity Types (list) ===
  async getTaxActivityTypes(): Promise<TaxActivityTypeItem[]> {
    const acqResponse = await httpClient.get<TaxActivityTypeItem[]>('/tax-activity-types');
    return acqResponse.data;
  },
  async createTaxActivityType(acqReq: CreateTaxActivityTypeRequest): Promise<string> {
    const acqResponse = await httpClient.post<string>('/tax-activity-types', acqReq);
    return acqResponse.data;
  },
  async updateTaxActivityType(acqReq: UpdateTaxActivityTypeRequest): Promise<void> {
    await httpClient.put(`/tax-activity-types/${acqReq.id}`, acqReq);
  },
  async deleteTaxActivityType(acqId: string): Promise<void> {
    await httpClient.delete(`/tax-activity-types/${acqId}`);
  },

  // === Settlement Types (list) ===
  async getSettlementTypes(): Promise<SettlementTypeItem[]> {
    const acqResponse = await httpClient.get<SettlementTypeItem[]>('/settlement-types');
    return acqResponse.data;
  },
  async createSettlementType(acqReq: CreateSettlementTypeRequest): Promise<string> {
    const acqResponse = await httpClient.post<string>('/settlement-types', acqReq);
    return acqResponse.data;
  },
  async updateSettlementType(acqReq: UpdateSettlementTypeRequest): Promise<void> {
    await httpClient.put(`/settlement-types/${acqReq.id}`, acqReq);
  },
  async deleteSettlementType(acqId: string): Promise<void> {
    await httpClient.delete(`/settlement-types/${acqId}`);
  },

  // === Organization Departments (list by orgId) ===
  async getOrgDepartments(acqOrgId: string): Promise<OrgDepartmentItem[]> {
    const acqResponse = await httpClient.get<OrgDepartmentItem[]>('/org-departments', { params: { organizationId: acqOrgId } });
    return acqResponse.data;
  },
  async createOrgDepartment(acqReq: CreateOrgDepartmentRequest): Promise<string> {
    const acqResponse = await httpClient.post<string>('/org-departments', acqReq);
    return acqResponse.data;
  },
  async updateOrgDepartment(acqReq: UpdateOrgDepartmentRequest): Promise<void> {
    await httpClient.put(`/org-departments/${acqReq.id}`, acqReq);
  },
  async deleteOrgDepartment(acqId: string): Promise<void> {
    await httpClient.delete(`/org-departments/${acqId}`);
  },

  // === VAT Rates (list, read-only) ===
  async getVatRates(): Promise<VatRateItem[]> {
    const acqResponse = await httpClient.get<VatRateItem[]>('/vat-rates');
    return acqResponse.data;
  },

  // === Counterparty Contracts (paginated) ===
  async getContracts(acqFilters: ContractFilters): Promise<PaginatedResult<ContractItem>> {
    const acqParams: Record<string, unknown> = { page: acqFilters.page, pageSize: acqFilters.pageSize };
    if (acqFilters.counterpartyId) acqParams.counterpartyId = acqFilters.counterpartyId;
    if (acqFilters.organizationId) acqParams.organizationId = acqFilters.organizationId;
    if (acqFilters.contractType) acqParams.contractType = acqFilters.contractType;
    if (acqFilters.search) acqParams.search = acqFilters.search;
    const acqResponse = await httpClient.get<PaginatedResult<ContractItem>>('/contracts', { params: acqParams });
    return acqResponse.data;
  },
  async createContract(acqReq: CreateContractRequest): Promise<string> {
    const acqResponse = await httpClient.post<string>('/contracts', acqReq);
    return acqResponse.data;
  },
  async updateContract(acqReq: UpdateContractRequest): Promise<void> {
    await httpClient.put(`/contracts/${acqReq.id}`, acqReq);
  },
  async deleteContract(acqId: string): Promise<void> {
    await httpClient.delete(`/contracts/${acqId}`);
  },

  // === Currency Rates (list with filters) ===
  async getCurrencyRates(acqFilters: { currencyId?: string; dateFrom?: string; dateTo?: string }): Promise<CurrencyRateItem[]> {
    const acqResponse = await httpClient.get<CurrencyRateItem[]>('/currency-rates', { params: acqFilters });
    return acqResponse.data;
  },
  async createCurrencyRate(acqReq: CreateCurrencyRateRequest): Promise<string> {
    const acqResponse = await httpClient.post<string>('/currency-rates', acqReq);
    return acqResponse.data;
  },
  async updateCurrencyRate(acqReq: UpdateCurrencyRateRequest): Promise<void> {
    await httpClient.put(`/currency-rates/${acqReq.id}`, acqReq);
  },
  async deleteCurrencyRate(acqId: string): Promise<void> {
    await httpClient.delete(`/currency-rates/${acqId}`);
  },

  // === Nomenclature Prices (paginated) ===
  async getNomenclaturePrices(acqFilters: { nomenclatureId?: string; priceTypeId?: string; page: number; pageSize: number }): Promise<PaginatedResult<NomenclaturePriceItem>> {
    const acqParams: Record<string, unknown> = { page: acqFilters.page, pageSize: acqFilters.pageSize };
    if (acqFilters.nomenclatureId) acqParams.nomenclatureId = acqFilters.nomenclatureId;
    if (acqFilters.priceTypeId) acqParams.priceTypeId = acqFilters.priceTypeId;
    const acqResponse = await httpClient.get<PaginatedResult<NomenclaturePriceItem>>('/nomenclature-prices', { params: acqParams });
    return acqResponse.data;
  },
  async createNomenclaturePrice(acqReq: Omit<NomenclaturePriceItem, 'id' | 'priceTypeName' | 'nomenclatureName' | 'currencyCode'>): Promise<string> {
    const acqResponse = await httpClient.post<string>('/nomenclature-prices', acqReq);
    return acqResponse.data;
  },
  async updateNomenclaturePrice(acqReq: NomenclaturePriceItem): Promise<void> {
    await httpClient.put(`/nomenclature-prices/${acqReq.id}`, acqReq);
  },
  async deleteNomenclaturePrice(acqId: string): Promise<void> {
    await httpClient.delete(`/nomenclature-prices/${acqId}`);
  },

  // === Accounting Policies (list by orgId) ===
  async getAccountingPolicies(acqOrgId: string): Promise<AccountingPolicyItem[]> {
    const acqResponse = await httpClient.get<AccountingPolicyItem[]>('/accounting-policies', { params: { organizationId: acqOrgId } });
    return acqResponse.data;
  },
  async createAccountingPolicy(acqReq: Omit<AccountingPolicyItem, 'id' | 'organizationName'>): Promise<string> {
    const acqResponse = await httpClient.post<string>('/accounting-policies', acqReq);
    return acqResponse.data;
  },
  async updateAccountingPolicy(acqReq: AccountingPolicyItem): Promise<void> {
    await httpClient.put(`/accounting-policies/${acqReq.id}`, acqReq);
  },
  async deleteAccountingPolicy(acqId: string): Promise<void> {
    await httpClient.delete(`/accounting-policies/${acqId}`);
  },

  // Account Balances
  async getAccountBalances(acqFilters: { organizationId?: string; dateFrom?: string; dateTo?: string }): Promise<AccountBalanceItem[]> {
    const acqResponse = await httpClient.get<AccountBalanceItem[]>('/account-balances', { params: acqFilters });
    return acqResponse.data;
  },

  // Trial Balance (ОСВ)
  async getTrialBalance(acqFilters: TrialBalanceFilters): Promise<TrialBalanceLine[]> {
    const acqResponse = await httpClient.get<TrialBalanceLine[]>('/trial-balance', { params: acqFilters });
    return acqResponse.data;
  },

  // Journal Entries (Проводки)
  async getJournalEntries(acqFilters: JournalEntryFilters): Promise<PaginatedResult<JournalEntryItem>> {
    const acqResponse = await httpClient.get<PaginatedResult<JournalEntryItem>>('/journal-entries', { params: acqFilters });
    return acqResponse.data;
  },
  async getJournalEntriesByDocument(acqDocId: string): Promise<JournalEntryItem[]> {
    const acqResponse = await httpClient.get<JournalEntryItem[]>(`/journal-entries/by-document/${acqDocId}`);
    return acqResponse.data;
  },

  // Individuals
  async getIndividuals(acqFilters: IndividualFilters): Promise<PaginatedResult<IndividualItem>> {
    const acqResponse = await httpClient.get<PaginatedResult<IndividualItem>>('/individuals', { params: acqFilters });
    return acqResponse.data;
  },
  async createIndividual(acqReq: CreateIndividualRequest): Promise<string> { return (await httpClient.post<string>('/individuals', acqReq)).data; },
  async updateIndividual(acqReq: UpdateIndividualRequest): Promise<void> { await httpClient.put(`/individuals/${acqReq.id}`, acqReq); },
  async deleteIndividual(acqId: string): Promise<void> { await httpClient.delete(`/individuals/${acqId}`); },

  // Employees
  async getEmployees(acqFilters: EmployeeFilters): Promise<PaginatedResult<EmployeeItem>> {
    const acqResponse = await httpClient.get<PaginatedResult<EmployeeItem>>('/employees', { params: acqFilters });
    return acqResponse.data;
  },
  async createEmployee(acqReq: CreateEmployeeRequest): Promise<string> { return (await httpClient.post<string>('/employees', acqReq)).data; },
  async updateEmployee(acqReq: UpdateEmployeeRequest): Promise<void> { await httpClient.put(`/employees/${acqReq.id}`, acqReq); },
  async deleteEmployee(acqId: string): Promise<void> { await httpClient.delete(`/employees/${acqId}`); },

  // Positions API видалено у Sprint 20+ Refactor Етап 4f. Position тепер meta-каталог;
  // CRUD через catalogItemsApi(typeCode='position'), reads через /api/references/position.

  // Fixed Assets
  async getFixedAssets(): Promise<FixedAssetItem[]> { return (await httpClient.get<FixedAssetItem[]>('/fixed-assets')).data; },
  async createFixedAsset(acqReq: CreateFixedAssetRequest): Promise<string> { return (await httpClient.post<string>('/fixed-assets', acqReq)).data; },
  async updateFixedAsset(acqReq: UpdateFixedAssetRequest): Promise<void> { await httpClient.put(`/fixed-assets/${acqReq.id}`, acqReq); },
  async deleteFixedAsset(acqId: string): Promise<void> { await httpClient.delete(`/fixed-assets/${acqId}`); },

  // Intangible Assets
  async getIntangibleAssets(): Promise<IntangibleAssetItem[]> { return (await httpClient.get<IntangibleAssetItem[]>('/intangible-assets')).data; },
  async createIntangibleAsset(acqReq: CreateIntangibleAssetRequest): Promise<string> { return (await httpClient.post<string>('/intangible-assets', acqReq)).data; },
  async updateIntangibleAsset(acqReq: UpdateIntangibleAssetRequest): Promise<void> { await httpClient.put(`/intangible-assets/${acqReq.id}`, acqReq); },
  async deleteIntangibleAsset(acqId: string): Promise<void> { await httpClient.delete(`/intangible-assets/${acqId}`); },
};
